import React, { useMemo, useState } from "react";
import { Box, Flex, Grid, GridItem, Select, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import IssbContainer from "../IssbContainer";
import PageHeader from "components/text/PageHeader";
import { H2Dashboard, H3 } from "components/text/headings";
import DashboardCard from "../Dashboard/components/DashboardCard";
import { DashboardNumber } from "../Dashboard/components/RequirementsCard";
import { getCompletenessConfidenceHeatmapData } from "../Dashboard/dashboardDataUtils";
import { gotoFilteredQuestionList } from "../Dashboard/components/AIDashboard/AIDashboard";
import { isTaskClosed, taskService } from "api/ai/ai.service";
import userAPI, { getUserName } from "api/user-administration/user-administration";
import { STATUSFIELD_VALUE_LABEL } from "../IssbDataInput/components/AdditionalStatuses";
import { nivo_theme } from "../Dashboard/components/chart-utils";
import { formatDate } from "utils";
import LocalSpinner from "components/LocalSpinner";
import withAuthorization from "context/Auth/WithAuthorization";

const COMPLETENESS_ORDER = { NONE: 0, LOW: 1, MEDIUM: 2, HIGH: 3 };

/**
 * Classifies every question appearing in either task's outputs into exactly one comparison bucket,
 * based on how its completeness changed between run A and run B.
 * @returns {{ [bucket: string]: string[] }} question ids per bucket
 */
function compareTaskOutputs(outputsA = {}, outputsB = {}) {
  const allIds = new Set([...Object.keys(outputsA), ...Object.keys(outputsB)]);
  const buckets = {
    noAnswerBoth: [],
    gained: [],
    lost: [],
    improved: [],
    regressed: [],
    unchanged: [],
    onlyInA: [],
    onlyInB: [],
  };

  allIds.forEach((qid) => {
    const inA = qid in outputsA;
    const inB = qid in outputsB;
    if (inA && !inB) {
      buckets.onlyInA.push(qid);
      return;
    }
    if (!inA && inB) {
      buckets.onlyInB.push(qid);
      return;
    }
    const completenessA = outputsA[qid]?.json_content?.completeness || "NONE";
    const completenessB = outputsB[qid]?.json_content?.completeness || "NONE";
    if (completenessA === "NONE" && completenessB === "NONE") {
      buckets.noAnswerBoth.push(qid);
    } else if (completenessA === "NONE") {
      buckets.gained.push(qid);
    } else if (completenessB === "NONE") {
      buckets.lost.push(qid);
    } else if (completenessA === completenessB) {
      buckets.unchanged.push(qid);
    } else if (COMPLETENESS_ORDER[completenessB] > COMPLETENESS_ORDER[completenessA]) {
      buckets.improved.push(qid);
    } else {
      buckets.regressed.push(qid);
    }
  });

  return buckets;
}

function TaskHeatmap({ title, outputs }) {
  const { data, qids } = useMemo(() => getCompletenessConfidenceHeatmapData(outputs), [outputs]);
  const navigate = useNavigate();

  const formatCompleteness = (key) => STATUSFIELD_VALUE_LABEL.completeness[key] ?? key;
  const formatConfidence = (key) => STATUSFIELD_VALUE_LABEL.confidence[key] ?? key;

  const handleClick = (cell) => {
    if (!cell?.value) return;
    const questionIds = qids[cell.serieId]?.[cell.data.x] || [];
    gotoFilteredQuestionList(
      navigate,
      questionIds,
      `${title}: ${formatCompleteness(cell.serieId)} completeness / ${formatConfidence(cell.data.x)} confidence`
    );
  };

  return (
    <Box>
      <H3>{title}</H3>
      <Box height="320px">
        <ResponsiveHeatMap
          data={data}
          margin={{ top: 50, right: 20, bottom: 20, left: 90 }}
          valueFormat=".0f"
          axisTop={{
            tickSize: 0,
            tickPadding: 8,
            legend: "Confidence",
            legendPosition: "middle",
            legendOffset: -30,
            format: (d) => formatConfidence(d),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            legend: "Completeness",
            legendPosition: "middle",
            legendOffset: -70,
            format: (d) => formatCompleteness(d),
          }}
          axisRight={null}
          axisBottom={null}
          colors={(d) => (d.data.y === 0 ? "#F7FAFC" : d.data.color)}
          labelTextColor={(d) => (d.data.y === 0 ? "#E2E8F0" : d.data.textColor)}
          borderWidth={2}
          borderColor="#FFFFFF"
          onClick={handleClick}
          tooltip={({ cell }) => (
            <Box borderRadius="lg" bg="pureWhite" border="1px solid" borderColor="lime" px={3} py={2}>
              <Text fontSize="sm" color="darkTeal">
                <Text as="span" fontWeight="bold">
                  Completeness:
                </Text>{" "}
                {formatCompleteness(cell.serieId)}
              </Text>
              <Text fontSize="sm" color="darkTeal">
                <Text as="span" fontWeight="bold">
                  Confidence:
                </Text>{" "}
                {formatConfidence(cell.data.x)}
              </Text>
              <Text fontSize="sm" color="darkTeal">
                <Text as="span" fontWeight="bold">
                  Questions:
                </Text>{" "}
                {cell.value}
              </Text>
            </Box>
          )}
          emptyColor="#555555"
          theme={nivo_theme}
        />
      </Box>
    </Box>
  );
}

/** e.g. "23 Jul 2026 — Jane Smith — All Requirements" */
function useTaskLabel(task) {
  const { data: user } = useQuery(["user", task?.user_id], () => userAPI.getUserById(task.user_id), {
    enabled: !!task?.user_id,
  });
  if (!task) return "";
  const scopeLabel = task.task_type === "question_answer" ? `${task.inputs?.question_ids?.length ?? "?"} requirements` : "";
  return [formatDate(task.created_date), user ? getUserName(user) : null, scopeLabel].filter(Boolean).join(" — ");
}

function TaskOption({ task }) {
  const label = useTaskLabel(task);
  return <option value={task.id}>{label}</option>;
}

const BUCKET_CARDS = [
  { key: "noAnswerBoth", title: "No answer in both" },
  { key: "gained", title: "Newly answered (A → B)" },
  { key: "lost", title: "Lost answer (A → B)" },
  { key: "improved", title: "Improved completeness" },
  { key: "regressed", title: "Regressed completeness" },
  { key: "unchanged", title: "Unchanged (both answered)" },
];

const CompareAIReviewsPage = () => {
  const navigate = useNavigate();
  const roundSelector = useSelector((state) => state.issbRound);
  const questionnaireId = roundSelector.selectedRound?.id;

  const { data: aiTasks, isLoading: loadingTasks } = useQuery(
    "taskService.list",
    () => taskService.list({ round_id: questionnaireId }),
    { enabled: !!questionnaireId, cacheTime: 0, staleTime: 0 }
  );

  const reviewTasks = useMemo(
    () =>
      (aiTasks || [])
        .filter((t) => t.task_type === "question_answer" && isTaskClosed(t))
        .sort((a, b) => new Date(b.created_date) - new Date(a.created_date)),
    [aiTasks]
  );

  const [taskAId, setTaskAId] = useState("");
  const [taskBId, setTaskBId] = useState("");

  const { data: taskA, isLoading: loadingA } = useQuery(["ai-task", taskAId], () => taskService.get(taskAId), {
    enabled: !!taskAId,
  });
  const { data: taskB, isLoading: loadingB } = useQuery(["ai-task", taskBId], () => taskService.get(taskBId), {
    enabled: !!taskBId,
  });

  const buckets = useMemo(
    () => compareTaskOutputs(taskA?.outputs || {}, taskB?.outputs || {}),
    [taskA, taskB]
  );

  const hasScopeMismatch = buckets.onlyInA.length > 0 || buckets.onlyInB.length > 0;

  return (
    <IssbContainer margin="10%">
      <PageHeader
        title="Compare AI Reviews"
        subTitle="Compare two AI Review runs within this round to see how completeness changed between them."
      />

      {loadingTasks ? (
        <LocalSpinner />
      ) : (
        <Flex gap={4} mb={2} sx={{ select: { _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" } } }}>
          <Box flex={1}>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              Review A
            </Text>
            <Select placeholder="Select a review" value={taskAId} onChange={(e) => setTaskAId(e.target.value)}>
              {reviewTasks.map((t) => (
                <TaskOption key={t.id} task={t} />
              ))}
            </Select>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              Review B
            </Text>
            <Select placeholder="Select a review" value={taskBId} onChange={(e) => setTaskBId(e.target.value)}>
              {reviewTasks.map((t) => (
                <TaskOption key={t.id} task={t} />
              ))}
            </Select>
          </Box>
        </Flex>
      )}

      {taskAId && taskBId && !loadingA && !loadingB && (
        <>
          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6} mb={8} mt={4}>
            <GridItem>
              <TaskHeatmap title="Review A" outputs={taskA?.outputs || {}} />
            </GridItem>
            <GridItem>
              <TaskHeatmap title="Review B" outputs={taskB?.outputs || {}} />
            </GridItem>
          </Grid>

          <H2Dashboard mb={4}>Comparison</H2Dashboard>
          <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
            {BUCKET_CARDS.map(({ key, title }) => (
              <GridItem key={key}>
                <DashboardCard>
                  <DashboardNumber
                    title={title}
                    titlePosition="start"
                    value={buckets[key].length}
                    onClick={() => gotoFilteredQuestionList(navigate, buckets[key], title)}
                  />
                </DashboardCard>
              </GridItem>
            ))}
          </Grid>

          {hasScopeMismatch && (
            <>
              <H2Dashboard mb={4}>Scope differences</H2Dashboard>
              <Text fontSize="sm" color="gray.500" mb={2}>
                These questions were only attempted in one of the two reviews (e.g. the runs covered different filters or scopes).
              </Text>
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
                <GridItem>
                  <DashboardCard>
                    <DashboardNumber
                      title="Only in Review A"
                      titlePosition="start"
                      value={buckets.onlyInA.length}
                      onClick={() => gotoFilteredQuestionList(navigate, buckets.onlyInA, "Only in Review A")}
                    />
                  </DashboardCard>
                </GridItem>
                <GridItem>
                  <DashboardCard>
                    <DashboardNumber
                      title="Only in Review B"
                      titlePosition="start"
                      value={buckets.onlyInB.length}
                      onClick={() => gotoFilteredQuestionList(navigate, buckets.onlyInB, "Only in Review B")}
                    />
                  </DashboardCard>
                </GridItem>
              </Grid>
            </>
          )}
        </>
      )}
    </IssbContainer>
  );
};

export default withAuthorization(CompareAIReviewsPage);
